/**
 * Co-agency share vocabulary — the one place that turns a stored share into words.
 *
 * A co-agency's cut is agreed one of two ways, and the row (or letter) says which
 * in a basis field (migration 20260821100000):
 *   PERCENTAGE — a rate applied to the professional fee: "30%"
 *   AMOUNT     — a flat sum regardless of the fee: "RM 5,000.00"
 *
 * On an AMOUNT row the percentage is 0 and carries no meaning, so anything that
 * prints "{value}%" without asking the basis first reports every flat-sum deal as
 * a co-agency owed nothing. Sale and letter screens ask here instead.
 *
 * The letter API speaks the compact PWA aliases (`share_basis`, `ca_share`,
 * `ca_share_amount`); the sale API speaks the column names. Each shape gets its
 * own reader rather than one that has to guess which it was handed.
 */
import { RM } from './format';

export const SHARE_BASIS_OPTIONS = [
  { label: 'Percentage', value: 'PERCENTAGE' },
  { label: 'Flat amount', value: 'AMOUNT' },
];

export const isAmountBasis = (basis) => basis === 'AMOUNT';

/** A sale co-agency / referral row's share — DB row or an in-progress form row. */
export function shareLabel(row, dash = '—') {
  if (!row) return dash;
  if (isAmountBasis(row.commission_basis)) {
    return row.commission_amount != null && row.commission_amount !== ''
      ? RM(row.commission_amount)
      : dash;
  }
  if (row.commission_percentage == null || row.commission_percentage === '') return dash;
  // "10%" alone on an after-co-agency referral reads as 10% of the fee — twice
  // what the referrer is actually paid on a 50/50 We-list deal.
  return referralBaseOf(row) === 'AFTER_CO_AGENCY'
    ? `${Number(row.commission_percentage)}% of CID after co-agency`
    : `${row.commission_percentage}%`;
}

/**
 * Which base a REFERRAL's percentage is taken from (migration 20260928101500) —
 * the client-side twin of the servers' utils/coAgencyShare.referralBaseOf, worded
 * exactly as the admin portal words it.
 *
 *   CID_PORTION      CID's base: the whole fee on a "We list" sale, CID's stated
 *                    portion on a "They list" one. The default.
 *   AFTER_CO_AGENCY  CID's base less what CID pays the "We list" co-agencies —
 *                    "10% of what CID remains" on a 50/50 deal is RM 500 on a
 *                    RM 10,000 fee, not RM 1,000.
 *
 * Only a PERCENTAGE referral can be AFTER_CO_AGENCY: a co-agency is never paid out
 * of another co-agency's leftovers, and a flat sum is a sum. Every other row reads
 * CID_PORTION whatever it carries, exactly as the engine reads it.
 */
export const REFERRAL_BASE_OPTIONS = [
  { label: "CID's portion (whole fee on We list)", short: "CID's portion", value: 'CID_PORTION' },
  { label: "CID's portion after co-agency", short: 'After co-agency', value: 'AFTER_CO_AGENCY' },
];

/** A referral row — form rows carry the type flat, server rows may embed it. */
export function isReferralRow(row) {
  return (row?.co_agency_type || row?.co_agencies?.co_agency_type) === 'REFERRAL';
}

export function referralBaseOf(row) {
  if (!isReferralRow(row) || isAmountBasis(row?.commission_basis)) return 'CID_PORTION';
  return row?.referral_base === 'AFTER_CO_AGENCY' ? 'AFTER_CO_AGENCY' : 'CID_PORTION';
}

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;
const isCobroke = (r) => r?.role === 'COBROKE' && !isReferralRow(r);
const rowShareRm = (row, base) => (isAmountBasis(row?.commission_basis)
  ? round2(row?.commission_amount)
  : round2(((Number(row?.commission_percentage) || 0) / 100) * base));

/**
 * What every co-agency / referral row takes off CID's base, in RM — the mirror of
 * the servers' `deductionAmounts`, so a preview here prices an after-co-agency
 * referral exactly as the commission engine will.
 *
 * Returns an array parallel to `rows`: `{ amount, base }` per row, `base` being the
 * RM the row's percentage was taken from. Two passes: every "We list" co-agency is
 * priced off `cidBase` first, then the referrals — an AFTER_CO_AGENCY one off what
 * is left, clamped at zero. A "They list" row is paid nothing by CID (it shapes
 * `cidBase` instead, see cidGciBase).
 */
export function deductionAmounts(rows, cidBase) {
  const base = round2(cidBase);
  const list = rows || [];
  const coAgencyPaid = list
    .filter((r) => !isCobroke(r) && !isReferralRow(r))
    .reduce((sum, r) => sum + rowShareRm(r, base), 0);
  const afterCoAgency = Math.max(0, round2(base - coAgencyPaid));
  return list.map((r) => {
    if (isCobroke(r)) return { amount: 0, base };
    const rowBase = referralBaseOf(r) === 'AFTER_CO_AGENCY' ? afterCoAgency : base;
    return { amount: rowShareRm(r, rowBase), base: rowBase };
  });
}

/**
 * The "10% of RM 5,000.00 (CID after co-agency) = RM 500.00" line for an
 * after-co-agency referral. `rows` is the whole row set the referral sits in and
 * `netGci` the sale's fee. Null for every other row — their figure is plain from
 * the label — and while the fee is not yet known.
 */
export function referralBaseSummary(row, rows, netGci) {
  if (referralBaseOf(row) !== 'AFTER_CO_AGENCY') return null;
  const fee = Number(netGci);
  if (!Number.isFinite(fee) || fee <= 0) return null;
  const list = rows || [];
  const i = list.indexOf(row);
  if (i < 0) return null;
  const mine = deductionAmounts(list, cidGciBase(list, fee))[i];
  return `${Number(row.commission_percentage) || 0}% of ${RM(mine.base)} (CID after co-agency) = ${RM(mine.amount)}`;
}

/** The co-agency's side of a LETTER (compact API shape). */
export function letterCoaShareLabel(letter, dash = '—') {
  if (!letter) return dash;
  if (isAmountBasis(letter.share_basis ?? letter.sharing_basis)) {
    const amt = letter.ca_share_amount ?? letter.coa_sharing_amount;
    return amt != null ? RM(amt) : dash;
  }
  const pct = letter.ca_share ?? letter.coa_sharing_ratio;
  return pct != null ? `${Number(pct)}%` : dash;
}

/**
 * CID's side of a LETTER.
 *
 * There is no flat CID amount by design: once the co-agency's cut is a fixed sum,
 * CID's is whatever is left of the fee actually received. Naming a second figure
 * would let the two halves disagree with the fee and with each other.
 */
export function letterCidShareLabel(letter, dash = '—') {
  if (!letter) return dash;
  if (isAmountBasis(letter.share_basis ?? letter.sharing_basis)) return 'balance';
  const pct = letter.cid_share ?? letter.cid_sharing_ratio;
  return pct != null ? `${Number(pct)}%` : dash;
}

/** One-line "COA x / CID y" summary for letter pickers and list rows. */
export function letterShareSummary(letter) {
  return `COA ${letterCoaShareLabel(letter)} / CID ${letterCidShareLabel(letter)}`;
}

/**
 * The sale co-agency row a linked letter implies.
 *
 * Attaching a letter to a sale adopts the terms that letter states — including
 * which of the two shapes those terms are in. Values come back as strings because
 * the sale form holds every numeric field as a string until submit.
 */
export function shareFromLetter(letter) {
  if (!letter) {
    return { commission_basis: 'PERCENTAGE', commission_percentage: '', commission_amount: '' };
  }
  if (isAmountBasis(letter.share_basis ?? letter.sharing_basis)) {
    const amt = letter.ca_share_amount ?? letter.coa_sharing_amount;
    return {
      commission_basis: 'AMOUNT',
      commission_percentage: '0',
      commission_amount: amt != null ? String(amt) : '',
    };
  }
  const pct = letter.ca_share ?? letter.coa_sharing_ratio;
  return {
    commission_basis: 'PERCENTAGE',
    commission_percentage: pct != null ? String(pct) : '',
    commission_amount: '',
  };
}

/**
 * Is this co-agency row's share filled in?
 *
 * A co-broke row is exempt — it shares nothing by definition. Otherwise exactly one
 * of the two figures has to be present, and which one depends on the basis, so the
 * check cannot be "is the percentage a number".
 */
export function isShareComplete(row) {
  if (!row) return false;
  // A "They list" row states CID's portion of the deal instead of the co-agency's
  // share. It used to be complete by definition — nothing to fill in — which is
  // exactly how a sale reached the engine with no CID portion at all.
  if (row.role === 'COBROKE') {
    if (isAmountBasis(row.cid_share_basis)) return parseFloat(row.cid_share_amount) > 0;
    return parseFloat(row.cid_share_percentage) > 0;
  }
  if (isAmountBasis(row.commission_basis)) return parseFloat(row.commission_amount) > 0;
  return parseFloat(row.commission_percentage) >= 0;
}

/** The co-agency row as the sale API takes it. */
export function sharePayload(row) {
  if (row?.role === 'COBROKE') {
    return {
      commission_basis: 'PERCENTAGE',
      commission_percentage: 0,
      commission_amount: null,
      referral_base: 'CID_PORTION',
      ...cidSharePayload(row),
    };
  }
  const cidShareCleared = { cid_share_basis: 'PERCENTAGE', cid_share_percentage: 0, cid_share_amount: null };
  if (isAmountBasis(row?.commission_basis)) {
    return {
      commission_basis: 'AMOUNT',
      commission_percentage: 0,
      commission_amount: Number(row.commission_amount) || 0,
      referral_base: 'CID_PORTION',
      ...cidShareCleared,
    };
  }
  return {
    commission_basis: 'PERCENTAGE',
    commission_percentage: Number(row?.commission_percentage) || 0,
    commission_amount: null,
    // Only a referral can carry AFTER_CO_AGENCY — referralBaseOf pins the rest.
    referral_base: referralBaseOf(row),
    ...cidShareCleared,
  };
}

/**
 * CID's portion of a "They list" deal (migration 20260821140000).
 *
 * On those sales the commission on the sale is the WHOLE deal — the other agency
 * holds the listing, banks the fee and pays CID a share — so the co-agency's own
 * share column is 0 and says nothing. This is the figure that actually drives the
 * sale's money, and the one the row has to show instead.
 */
export function cidShareLabel(row, dash = '—') {
  if (!row) return dash;
  if (isAmountBasis(row.cid_share_basis)) {
    return row.cid_share_amount != null ? RM(row.cid_share_amount) : dash;
  }
  return row.cid_share_percentage != null ? `${row.cid_share_percentage}%` : dash;
}

/**
 * CID's portion in RM, given the deal's fee. Returns null when the row does not
 * state one — a caller showing a live "CID gets…" line has nothing to show yet.
 */
export function cidShareAmountOf(row, netGci) {
  const fee = Number(netGci);
  if (!row || !Number.isFinite(fee)) return null;
  if (isAmountBasis(row.cid_share_basis)) {
    const amount = Number(row.cid_share_amount);
    return Number.isFinite(amount) && amount > 0 ? Math.min(amount, fee) : null;
  }
  const pct = Number(row.cid_share_percentage);
  if (!Number.isFinite(pct) || pct <= 0) return null;
  return Math.min(Math.round((pct / 100) * fee * 100) / 100, fee);
}

/**
 * The live "deal RM x · they keep RM y · CID gets RM z" line under a They-list
 * row. Spelling all three out is the point: the whole reason for these columns is
 * that keying only the deal used to pay everyone on the other agency's half.
 */
export function dealSplitSummary(row, netGci) {
  const fee = Number(netGci);
  const ours = cidShareAmountOf(row, fee);
  if (!Number.isFinite(fee) || fee <= 0 || ours == null) return null;
  return `Deal ${RM(fee)} · they keep ${RM(fee - ours)} · CID gets ${RM(ours)}`;
}

/** CID's portion, as the sale API takes it. */
export function cidSharePayload(row) {
  if (isAmountBasis(row?.cid_share_basis)) {
    return {
      cid_share_basis: 'AMOUNT',
      cid_share_percentage: 0,
      cid_share_amount: Number(row.cid_share_amount) || 0,
    };
  }
  return {
    cid_share_basis: 'PERCENTAGE',
    cid_share_percentage: Number(row?.cid_share_percentage) || 0,
    cid_share_amount: null,
  };
}

/**
 * The slice of the fee CID's own side of the deal runs on — the client-side twin
 * of the server's utils/coAgencyShare.cidGciBase.
 *
 * The whole fee on an ordinary sale; CID's stated portion on a "They list" one,
 * where the commission on the sale is the deal the OTHER agency collects. Any
 * preview that quotes an agent their cut has to use this, or it promises them a
 * share of money that never reaches CID.
 *
 * Form rows carry `co_agency_type` flat rather than the server's embedded
 * `co_agencies.co_agency_type`, so the referral guard reads both.
 */
export function cidGciBase(rows, netGci) {
  const fee = Number(netGci);
  if (!Number.isFinite(fee) || fee <= 0) return 0;
  const cobroke = (rows || []).filter((r) => r
    && r.role === 'COBROKE'
    && (r.co_agency_type || r.co_agencies?.co_agency_type) !== 'REFERRAL');
  if (!cobroke.length) return fee;
  return cobroke.reduce((lowest, row) => {
    const ours = cidShareAmountOf(row, fee);
    return ours == null ? lowest : Math.min(lowest, ours);
  }, fee);
}

/**
 * A co-agency row's share as one label, whichever side of the deal it is on.
 *
 * A "We list" row states what CID pays them; a "They list" row states what CID
 * keeps and pays them nothing. Printing shareLabel for both reported the agency
 * holding the entire fee as taking 0.
 */
export function rowShareLabel(row, dash = '—', { rows = null, netGci = null } = {}) {
  if (row?.role !== 'COBROKE') {
    const label = shareLabel(row, dash);
    // An after-co-agency referral's RM is not readable off its label, so a caller
    // that hands over the row set and CID's base gets the priced figure with it.
    if (rows && referralBaseOf(row) === 'AFTER_CO_AGENCY' && Number(netGci) > 0) {
      const i = rows.indexOf(row);
      if (i >= 0) return `${label} = ${RM(deductionAmounts(rows, cidGciBase(rows, netGci))[i].amount)}`;
    }
    return label;
  }
  return `CID ${cidShareLabel(row, dash)}`;
}
